<script>
import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { supportsHtmlInCanvas, createLiquid } from "./liquidEngine.js";

export default defineComponent({
  props: {
    simResolution: { type: Number, default: undefined },
    dyeResolution: { type: Number, default: undefined },
    densityDissipation: { type: Number, default: undefined },
    velocityDissipation: { type: Number, default: undefined },
    pressure: { type: Number, default: undefined },
    pressureIterations: { type: Number, default: undefined },
    curl: { type: Number, default: undefined },
    radius: { type: Number, default: undefined },
    force: { type: Number, default: undefined },
    intensity: { type: Number, default: undefined },
    distortion: { type: Number, default: undefined },
    blend: { type: Number, default: undefined },
    color: { type: Array, default: undefined },
    rainbow: { type: Boolean, default: undefined },
  },
  setup(props) {
    const sourceEl = ref(null);
    const contentEl = ref(null);
    const outputEl = ref(null);
    const native = ref(false);

    let instance = null;
    let disposed = false;

    onMounted(async () => {
      native.value = supportsHtmlInCanvas();
      await nextTick();
      if (disposed) return;
      if (sourceEl.value && contentEl.value && outputEl.value) {
        instance = createLiquid(
          {
            source: sourceEl.value,
            content: contentEl.value,
            output: outputEl.value,
          },
          props,
        );
        if (native.value && !instance) {
          native.value = false;
          await nextTick();
          if (disposed) return;
          if (sourceEl.value && contentEl.value && outputEl.value) {
            instance = createLiquid(
              {
                source: sourceEl.value,
                content: contentEl.value,
                output: outputEl.value,
              },
              props,
            );
          }
        }
      }
    });

    onBeforeUnmount(() => {
      disposed = true;
      instance?.destroy();
      instance = null;
    });

    watch(
      () => ({ ...props }),
      (next) => instance?.setOptions(next),
      { deep: true },
    );

    return {
      sourceEl,
      contentEl,
      outputEl,
      native,
    };
  },
});
</script>

<template>
  <div style="position: relative; width: 100%; height: 100%">
    <canvas
      ref="sourceEl"
      layoutsubtree="true"
      :style="
        native
          ? 'position: absolute; inset: 0; width: 100%; height: 100%'
          : 'display: none'
      "
    >
      <div
        v-if="native"
        ref="contentEl"
        style="position: relative; width: 100%; height: 100%; overflow: auto"
      >
        <slot />
      </div>
    </canvas>
    <div
      v-if="!native"
      ref="contentEl"
      style="position: relative; width: 100%; height: 100%; overflow: auto"
    >
      <slot />
    </div>
    <canvas
      ref="outputEl"
      aria-hidden="true"
      style="
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      "
    />
  </div>
</template>
